import {
    ButtonView,
    ContextualBalloon,
    Editor,
    FocusTracker,
    KeystrokeHandler,
    type Locale,
    Plugin,
    type TemplateDefinition,
    View
} from 'ckeditor5';
import {ContentService} from "../_services/content.service";
import {SnackbarService} from "../_services/snackbar.service";
import {Subscription} from 'rxjs';
import {CommunitiesService} from "../_services/communities.service";

type AiAssistantServices = {
    contentService: ContentService;
    snackbarService: SnackbarService;
    communitiesService: CommunitiesService;
};

export default class AiAssistantPlugin extends Plugin {
    static get pluginName() {
        return 'AiAssistantPlugin' as const;
    }

    init(): void {
        const editor: Editor = this.editor;
        const balloon: ContextualBalloon = editor.plugins.get(ContextualBalloon);
        const services = editor.config.get('aiAssistant') as AiAssistantServices;
        if (!services?.contentService || !services?.snackbarService || !services?.communitiesService) {
            throw new Error('AiAssistantPlugin: Missing aiAssistant contentService/snackbarService/communitiesService in editor config.');
        }

        const formView = new AiAssistantFormView(editor.locale, services.contentService, services.snackbarService, services.communitiesService, editor);

        editor.ui.componentFactory.add('aiAssistant', (locale): ButtonView => {
            const button = new ButtonView(locale);

            button.set({
                label: 'AI',
                tooltip: true,
                withText: true
            });

            button.on('execute', (): void => {
                if (balloon.hasView(formView)) {
                    balloon.remove(formView);
                    return;
                }
                const targetEl: HTMLElement = button.element || editor.ui.getEditableElement()!;

                balloon.add({
                    view: formView,
                    position: {
                        target: targetEl
                    }
                });

                formView.focus();
            });

            return button;
        });

        // Keep position updated while open
        editor.editing.view.document.on('change:selection', (): void => {
            if (balloon.hasView(formView)) {
                balloon.updatePosition();
            }
        });

        formView.on('close', (): void => {
            if (balloon.hasView(formView)) balloon.remove(formView);
        });


        this.on('destroy', (): void => {
            if (balloon.hasView(formView)) balloon.remove(formView);
            formView.destroy();
        });
    }
}

class AiAssistantFormView extends View {
    public readonly focusTracker: FocusTracker;
    public readonly keystrokes: KeystrokeHandler;
    public textareaEl!: HTMLTextAreaElement;
    private readonly subs: Subscription = new Subscription();

    constructor(
        locale: Locale,
        private contentService: ContentService,
        private snackbarService: SnackbarService,
        private communitiesService: CommunitiesService,
        private editor: any
    ) {
        super(locale);

        this.focusTracker = new FocusTracker();
        this.keystrokes = new KeystrokeHandler();

        const template: TemplateDefinition = {
            tag: 'form',
            attributes: {
                class: ['ck', 'ck-ai-assistant-form'],
                tabindex: '-1'
            },
            // @ts-ignore
            children: [
                {
                    tag: 'textarea',
                    attributes: {
                        rows: '6',
                        class: ['ck', 'ck-ai-assistant-form__textarea']
                    }
                },
                {
                    tag: 'div',
                    attributes: {class: ['ck', 'ck-ai-assistant-form__actions']},
                    children: [
                        {
                            tag: 'button',
                            attributes: {
                                type: 'button',
                                class: ['ck', 'ck-button', 'ck-ai-assistant-cancel']
                            },
                            children: [{text: 'Cancel'}]
                        },
                        {
                            tag: 'button',
                            attributes: {
                                type: 'button',
                                class: ['ck', 'ck-button', 'ck-ai-assistant-insert']
                            },
                            children: [{text: 'Generate Post'}]
                        }
                    ]
                }
            ]
        };

        this.setTemplate(template);
    }

    override render(): void {
        let currentCommunityId: number | undefined
        let loadingInterval: number | null = null;

        this.subs.add(
            this.communitiesService.getCurrentDashboardCommunity().subscribe(community => {
                currentCommunityId = community?.id;
            })
        );

        super.render();

        const root = this.element as HTMLElement;
        this.textareaEl = root.querySelector('textarea') as HTMLTextAreaElement;
        const insertBtn = root.querySelector('.ck-ai-assistant-insert') as HTMLButtonElement;
        const cancelBtn = root.querySelector('.ck-ai-assistant-cancel') as HTMLButtonElement;
        this.textareaEl.maxLength = 1000;
        this.textareaEl.minLength = 10;
        this.textareaEl.placeholder = 'Please enter a description of the post you would like created ';
        insertBtn.addEventListener('click', (): void => {
            if (currentCommunityId) {
                disableForm()
                const textFieldValue: string = this.getValue()

                startLoading()
                this.contentService.generateAIPostContentAndListenForResponse(currentCommunityId, textFieldValue)
                    .subscribe({
                        next: (res): void => {
                            const text = res?.data?.result ?? '';
                            if (text) {
                                this.editor.model.change((writer: any): void => {
                                    const insertPos = this.editor.model.document.selection.getFirstPosition();
                                    writer.insertText(text, insertPos);
                                });
                                stopLoading('')
                                enableForm()
                                this.fire('close');
                            } else {
                                stopLoading(textFieldValue)
                                enableForm()
                                this.snackbarService.openErrorSnackBar(res?.data?.error ? 'Error: ' + res?.data?.error : 'API turned and invalid response please try again.');
                            }
                        },
                        error: (e): void => {
                            console.error(e)
                            stopLoading(textFieldValue)
                            this.snackbarService.openErrorSnackBar(e)
                            enableForm()
                        }
                    });

            }
        });
        cancelBtn.addEventListener('click', (): void => {
            this.textareaEl.value = '';
            this.fire('close');
        });

        const disableForm = (): void => {
            insertBtn.disabled = true;
            insertBtn.classList.add('disabled')
            cancelBtn.disabled = true;
            cancelBtn.classList.add('disabled')
            this.textareaEl.readOnly = true;
        }

        const enableForm = (): void => {
            insertBtn.disabled = false;
            insertBtn.classList.remove('disabled')
            cancelBtn.disabled = false;
            cancelBtn.classList.remove('disabled')
            this.textareaEl.readOnly = false;
        }

        const startLoading = (): void => {
            let dotCount = 0;
            this.textareaEl.value = 'Loading';

            loadingInterval = window.setInterval((): void => {
                dotCount = (dotCount + 1) % 4; // 0 → 3 then reset

                const dots = '.'.repeat(dotCount);
                this.textareaEl.value = 'Loading' + dots;
            }, 300);
        }

        const stopLoading = (text: string): void => {
            if (loadingInterval !== null) {
                clearInterval(loadingInterval);
                loadingInterval = null;
            }

            this.textareaEl.value = text;
        }

        this.focusTracker.add(this.textareaEl);
        this.keystrokes.listenTo(this.element!);

        this.keystrokes.set('Esc', (data, cancel): void => {
            cancel();
            this.fire('close');
        });
    }

    override destroy(): void {
        this.subs.unsubscribe();
        super.destroy();
        this.focusTracker.destroy();
        this.keystrokes.destroy();
    }

    getValue(): string {
        return this.textareaEl?.value ?? '';
    }

    focus(): void {
        this.textareaEl?.focus();
    }
}
